/**
 * salesByDimension.ts — Pure aggregation of a period's sales sliced four ways:
 * by category, by customer, by hour of day, and by weekday. The Sales report
 * answers "how much"; this answers "from what, from whom, and when".
 *
 * Everything is net of returns (a credit note, sign −1, subtracts) so the slices
 * reconcile with the Sales report's net total. Category and customer revenue are
 * measured on the line / invoice total (tax-inclusive), matching the Sales
 * report's top-products and payment figures.
 *
 * Pure — see `salesByDimensionDataSource.ts` for the WatermelonDB bridge.
 */

export const CREDIT_NOTE = 'CREDIT_NOTE';

export function signOf(type: string): 1 | -1 {
  return type === CREDIT_NOTE ? -1 : 1;
}

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface DimInvoiceInput {
  type: string;
  totalAmount: number;
  createdAt: Date;
  customerId: string | null;
}

export interface DimLineInput {
  productId: string;
  totalAmount: number;
  quantity: number;
  invoiceType: string;
}

export interface SalesByDimensionInput {
  invoices: DimInvoiceInput[];
  lines: DimLineInput[];
  /** productId → category name (resolved, with fallback). */
  productCategory: Map<string, string>;
  /** customerId → display name. */
  customerName: Map<string, string>;
}

// ─── Output ──────────────────────────────────────────────────────────────────

export interface DimRow {
  key: string;
  label: string;
  revenue: number;
  quantity: number;
  count: number;
}

export interface SalesByDimension {
  byCategory: DimRow[];
  /** Top customers by net spend (walk-ins grouped under one row). */
  byCustomer: DimRow[];
  /** Net revenue by hour of day, index 0–23. */
  byHour: number[];
  /** Net revenue by weekday, index 0 = Monday … 6 = Sunday. */
  byWeekday: number[];
  totalRevenue: number;
}

const WALK_IN = 'Walk-in';

export function buildSalesByDimension(input: SalesByDimensionInput): SalesByDimension {
  const { invoices, lines, productCategory, customerName } = input;

  // ── By category (line level) ──
  const catMap = new Map<string, { revenue: number; quantity: number; count: number }>();
  for (const li of lines) {
    const sign = signOf(li.invoiceType);
    const category = productCategory.get(li.productId) ?? 'Uncategorised';
    const acc = catMap.get(category) ?? { revenue: 0, quantity: 0, count: 0 };
    acc.revenue += sign * li.totalAmount;
    acc.quantity += sign * li.quantity;
    acc.count += 1;
    catMap.set(category, acc);
  }

  // ── By customer, hour, weekday (invoice level) ──
  const custMap = new Map<string, { revenue: number; count: number }>();
  const byHour = new Array(24).fill(0);
  const byWeekday = new Array(7).fill(0);
  let totalRevenue = 0;

  for (const inv of invoices) {
    const sign = signOf(inv.type);
    const value = sign * inv.totalAmount;
    totalRevenue += value;

    const key = inv.customerId ?? WALK_IN;
    const acc = custMap.get(key) ?? { revenue: 0, count: 0 };
    acc.revenue += value;
    if (inv.type !== CREDIT_NOTE) acc.count += 1;
    custMap.set(key, acc);

    byHour[inv.createdAt.getHours()] += value;
    // JS getDay: 0 = Sunday. Remap to Monday-first (0 = Mon … 6 = Sun).
    byWeekday[(inv.createdAt.getDay() + 6) % 7] += value;
  }

  const byCategory: DimRow[] = [...catMap.entries()]
    .map(([key, v]) => ({ key, label: key, revenue: round2(v.revenue), quantity: round2(v.quantity), count: v.count }))
    .filter((r) => r.revenue !== 0 || r.quantity !== 0)
    .sort((a, b) => b.revenue - a.revenue);

  const byCustomer: DimRow[] = [...custMap.entries()]
    .map(([key, v]) => ({
      key,
      label: key === WALK_IN ? WALK_IN : customerName.get(key) ?? 'Unknown customer',
      revenue: round2(v.revenue),
      quantity: 0,
      count: v.count,
    }))
    .filter((r) => r.revenue !== 0)
    .sort((a, b) => b.revenue - a.revenue);

  return {
    byCategory,
    byCustomer,
    byHour: byHour.map(round2),
    byWeekday: byWeekday.map(round2),
    totalRevenue: round2(totalRevenue),
  };
}
